import Dexie from 'dexie';

// IndexedDB Database for offline storage
export class SparkleDatabase extends Dexie {
  constructor() {
    super('SparkleWorld');

    this.version(1).stores({
      diary: '++id, date, mood, created_at',
      gameScores: '++id, game_name, score, created_at',
      achievements: '++id, type, category, created_at',
      crafts: '++id, craft_id, progress, completed',
      dailyActivities: '++id, date, unlocked_at',
      booksCache: 'id, lastUpdated',
      triviaCache: 'id, lastUpdated',
      settings: 'key'
    });

    // Version 2: Add encrypted vault metadata
    this.version(2).stores({
      vaultMeta: 'id' // Stores vault salt and wrapped DEK
    });
  }
}

export const db = new SparkleDatabase();

// Helper functions for common operations
export const dbHelpers = {
  // Diary operations
  async saveDiaryEntry(entry) {
    return await db.diary.add(entry);
  },

  async getDiaryEntries(limit = 50) {
    return await db.diary.orderBy('created_at').reverse().limit(limit).toArray();
  },

  async getDiaryByDate(date) {
    return await db.diary.where('date').equals(date).first();
  },

  // Game operations
  async saveGameScore(score) {
    return await db.gameScores.add(score);
  },

  async getGameScores(gameName = null, limit = 50) {
    if (gameName) {
      return await db.gameScores.where('game_name').equals(gameName).reverse().limit(limit).toArray();
    }
    return await db.gameScores.orderBy('created_at').reverse().limit(limit).toArray();
  },

  async getHighScore(gameName) {
    const scores = await db.gameScores.where('game_name').equals(gameName).reverse().sortBy('score');
    return scores.length > 0 ? scores[0] : { score: 0 };
  },

  // Achievement operations
  async addAchievement(achievement) {
    return await db.achievements.add(achievement);
  },

  async getAchievementsSummary() {
    const achievements = await db.achievements.toArray();
    const summary = {
      total_stars: 0,
      magic_eggs: 0,
      by_category: {}
    };

    achievements.forEach(ach => {
      if (ach.type === 'star') {
        summary.total_stars += ach.count;
      } else if (ach.type === 'magic_egg') {
        summary.magic_eggs += ach.count;
      }

      if (!summary.by_category[ach.category]) {
        summary.by_category[ach.category] = { stars: 0, eggs: 0 };
      }

      if (ach.type === 'star') {
        summary.by_category[ach.category].stars += ach.count;
      } else if (ach.type === 'magic_egg') {
        summary.by_category[ach.category].eggs += ach.count;
      }
    });

    return summary;
  },

  // Craft operations
  async saveCraftProgress(craft) {
    const existing = await db.crafts.where('craft_id').equals(craft.craft_id).first();
    if (existing) {
      return await db.crafts.update(existing.id, craft);
    }
    return await db.crafts.add(craft);
  },

  async getCraftProgress(craftId) {
    return await db.crafts.where('craft_id').equals(craftId).first();
  },

  async getAllCrafts() {
    return await db.crafts.toArray();
  },

  async getAllCraftProgress() {
    return await dbHelpers.getAllCrafts();
  },

  // Daily activity operations
  async getTodayActivity() {
    const today = new Date().toISOString().split('T')[0];
    return await db.dailyActivities.where('date').equals(today).first();
  },

  async unlockDailyActivity(activityData) {
    const today = new Date().toISOString().split('T')[0];
    return await db.dailyActivities.add({
      date: today,
      activity_data: activityData,
      unlocked_at: new Date().toISOString()
    });
  },

  // Cache operations
  async cacheBooks(books) {
    return await db.booksCache.put({
      id: 'children_books',
      data: books,
      lastUpdated: new Date().toISOString()
    });
  },

  async getCachedBooks() {
    const cached = await db.booksCache.get('children_books');
    if (!cached) return null;

    const lastUpdated = new Date(cached.lastUpdated);
    const now = new Date();
    const daysDiff = (now - lastUpdated) / (1000 * 60 * 60 * 24);

    if (daysDiff > 7) {
      await db.booksCache.delete('children_books');
      return null;
    }

    return cached.data;
  },

  async cacheTrivia(trivia) {
    return await db.triviaCache.put({
      id: 'trivia_questions',
      data: trivia,
      lastUpdated: new Date().toISOString()
    });
  },

  async getCachedTrivia() {
    const cached = await db.triviaCache.get('trivia_questions');
    if (!cached) return null;

    const lastUpdated = new Date(cached.lastUpdated);
    const now = new Date();
    const daysDiff = (now - lastUpdated) / (1000 * 60 * 60 * 24);

    if (daysDiff > 1) {
      await db.triviaCache.delete('trivia_questions');
      return null;
    }

    return cached.data;
  },

  // Settings
  async getSetting(key, defaultValue = null) {
    const setting = await db.settings.get(key);
    return setting ? setting.value : defaultValue;
  },

  async setSetting(key, value) {
    return await db.settings.put({ key, value });
  },

  async isBookRead(bookId) {
    if (!bookId) return false;
    const achievement = await db.achievements
      .where('book_id')
      .equals(bookId)
      .first();
    return !!achievement;
  }
};
